import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../helpers/axios";
import { userActions } from "../actions/user.actions";
import { appContextConstants } from '../constants/appContext.constants';
const FILTER_KEY = "disbursedTableFilters";
const getSavedFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) || "null") || {};
  } catch {
    return {};
  }
};
const useDisbursedData = ({ status, user, dependencies = {} }) => {
  const productTypeSelected = useSelector((state) => state.ProductTypeFilter.selectedProductType);
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    sessionStorage.getItem("page")
      ? parseInt(sessionStorage.getItem("page"), 10)
      : 1
  );
  const saved = getSavedFilters();
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalFetchRows, setTotalFetchRows] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterDateFrom, setFilterDateFrom] = useState(saved.filterDateFrom || null);
  const [filterDateTo, setFilterDateTo] = useState(saved.filterDateTo || null);
  const [instituteId, setInstituteId] = useState(saved.instituteId || null);
  const [instituteName, setInstituteName] = useState(saved.instituteName || "");
  const [selectedChannel, setSelectedChannel] = useState(saved.selectedChannel || "");
  const [activePage, setActivePage] = useState(15);

  const buildQueryParams = useCallback(
    (overrides = {}) => {
      const params = {};

      if (filterDateFrom && filterDateTo) {
        params.fromDate = filterDateFrom;
        params.toDate = filterDateTo + " 23:59:59";
      }

      if (instituteId) {
        params.instituteId = instituteId;
      }

      if (status) {
        params.status = status;
      }

      if (selectedChannel) {
        params.channel = selectedChannel;
      }
      if (productTypeSelected && productTypeSelected !== "all") {
        params.productType = Number(productTypeSelected);
      }
      params.perPage = rowsPerPage;
      params.pageNo = currentPage;

      return { ...params, ...overrides };
    },
    [
      filterDateFrom,
      filterDateTo,
      instituteId,
      status,
      selectedChannel,
      productTypeSelected,
      rowsPerPage,
      currentPage,
    ]
  );

  const fetchData = useCallback(
    async (queryOverrides = {}) => {
      setIsLoading(true);

      try {
        const queryParams = buildQueryParams(queryOverrides);

        const response = await axiosInstance.get(
          "/admin/application/disbursed",
          {
            params: queryParams,
            headers: {
            },
          }
        );

        const data = response?.data?.data;
        setTableData(data?.details || []);
        setCurrentPage(data?.pagination?.currentPage || 1);
        setTotalFetchRows(data?.pagination?.totalRows || 0);
        setRowsPerPage(data?.pagination?.perPage || 15);
        return data;
      } catch (error) {
        console.error("Error fetching disbursed data:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [buildQueryParams, user]
  );

  const handlePageChange = useCallback(
    (pageNumber) => {
      setCurrentPage(pageNumber);
      fetchData({ pageNo: pageNumber });
    },
    [fetchData]
  );

  const handleRowsPerPageChange = useCallback(
    (number) => {
      setActivePage(number);
      setRowsPerPage(number);
      fetchData({ perPage: number, pageNo: 1 });
    },
    [fetchData]
  );

  const handleSearch = useCallback(() => {
    fetchData({ pageNo: 1 });
  }, [fetchData]);

  const handleDateFilter = useCallback((startDate, endDate) => {
    setFilterDateFrom(startDate);
    setFilterDateTo(endDate);
  }, []);

  const clearFilters = useCallback(() => {
    sessionStorage.removeItem(FILTER_KEY);
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setInstituteId(null);
    setInstituteName("");
    setSelectedChannel("");
    setCurrentPage(1);
    setActivePage(15);
    fetchData({
      fromDate: undefined,
      toDate: undefined,
      instituteId: undefined,
      channel: undefined,
      pageNo: 1,
      perPage: 15,
    });
  }, [fetchData]);

  const handleChannelChange = useCallback(
    (channelValue) => {
      setSelectedChannel(channelValue);
      fetchData({ channel: channelValue, pageNo: 1 });
    },
    [fetchData]
  );
  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({
        filterDateFrom,
        filterDateTo,
        instituteId,
        instituteName,
        selectedChannel,
      })
    );
  }, [filterDateFrom, filterDateTo, instituteId, instituteName, selectedChannel]);

  useEffect(() => {
    const shouldFetch =
      !dependencies.usersearchdefstate &&
      !dependencies.usersearchinsstate;

    if (shouldFetch) {
      dispatch({ type: appContextConstants.SET_APP_CONTEXT, payload: { status } });
      fetchData();
    } else {
      if (dependencies.usersearchdefstate) {
        setIsLoading(false);
        setTableData(dependencies.usersearchdefstate?.details || []);
        setCurrentPage(
          dependencies.usersearchdefstate?.pagination?.currentPage || 1
        );
        setTotalFetchRows(
          dependencies.usersearchdefstate?.pagination?.totalRows || 0
        );
        setRowsPerPage(
          dependencies.usersearchdefstate?.pagination?.perPage || 15
        );
      }
      if (dependencies.usersearchinsstate) {
        setIsLoading(false);
        setTableData(dependencies.usersearchinsstate || []);
      }
    }
  }, [
    status,
    productTypeSelected,
    dependencies.insubstate,
    dependencies.datestate,
    dependencies.usersearchdefstate,
    dependencies.usersearchinsstate,
  ]);

  return {
    tableData,
    productTypeSelected,
    currentPage,
    rowsPerPage,
    totalFetchRows,
    isLoading,
    filterDateFrom,
    filterDateTo,
    instituteId,
    instituteName,
    selectedChannel,
    activePage,

    setFilterDateFrom,
    setFilterDateTo,
    setInstituteId,
    setInstituteName,
    setSelectedChannel,
    setCurrentPage,

    fetchData,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleDateFilter,
    clearFilters,
    handleChannelChange,
  };
};

export default useDisbursedData;
